import React, { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, Menu, MenuItem, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tokens } from "../theme";

// Portal-styled dropdown menu (MUI "customized menu" pattern, adapted to v5).
const StyledMenu = styled(Menu)(({ theme }) => {
  const colors = tokens(theme.paletteId);
  return {
    "& .MuiPaper-root": {
      marginTop: "4px",
      minWidth: 148,
      borderRadius: 6,
      border: `1px solid ${colors.menuBorder}`,
      boxShadow: "0 6px 20px rgba(16,24,40,.14)",
      color: colors.ctlInk,
      "& .MuiMenu-list": { padding: "4px" },
      "& .MuiMenuItem-root": {
        height: 30,
        minHeight: 30,
        padding: "0 10px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 550,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        "&:hover": { backgroundColor: colors.openRow, color: colors.btnHover },
      },
    },
  };
});

// Tinted rather than filled: the row still needs a decision, but the table
// body carries no filled buttons.
const triggerStyles = (colors) => ({
  width: "100%",
  justifyContent: "space-between",
  height: "28px",
  minHeight: "28px",
  padding: "0 7px 0 11px",
  borderRadius: "6px",
  border: `1px solid ${colors.ctlBorderHover}`,
  backgroundColor: colors.actionTintBg,
  color: colors.btnHover,
  fontSize: "11px",
  fontWeight: 500,
  letterSpacing: "0.05em",
  lineHeight: 1,
  textTransform: "uppercase",
  "&:hover": { backgroundColor: colors.actionTintBg, borderColor: colors.btn },
  "& .MuiButton-endIcon": {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: "7px",
    borderLeft: `1px solid ${colors.ctlBorderHover}`,
    "& svg": { fontSize: "13px" },
  },
  "&.Mui-disabled": {
    backgroundColor: colors.disBg,
    borderColor: colors.menuBorder,
    color: colors.disFg,
  },
});

const DropDownButton = ({ row, disabled, handleHibernateWakeUp }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const currentOption =
    row?.status === "HIBERNATE_FAILED" ? "HIBERNATE" : "WAKE UP";
  const [buttonContent, setButtonContent] = useState(currentOption);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (action) => {
    setButtonContent(action);
    handleClose();
    handleHibernateWakeUp(row, action);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Button
        id="dropdown-button"
        aria-controls={open ? "dropdown-menu" : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        variant="text"
        disableElevation
        disableRipple
        disabled={disabled}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={triggerStyles(colors)}
      >
        {buttonContent}
      </Button>
      <StyledMenu
        id="dropdown-menu"
        MenuListProps={{ "aria-labelledby": "dropdown-button" }}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {["WAKE UP", "HIBERNATE"].map((action) => (
          <MenuItem
            key={action}
            onClick={() => handleSelect(action)}
            disableRipple
          >
            {action}
          </MenuItem>
        ))}
      </StyledMenu>
    </Box>
  );
};

export default DropDownButton;
